/**
 * SocialSignIn
 *
 * Secondary sign-in options: Google OAuth and Enterprise. Defers auth
 * orchestration to parent handlers and includes safety timeouts.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaGoogle } from "react-icons/fa";
import { Building2 } from 'lucide-react';
import { auth } from '../../../firebase/firebase';

export default function SocialSignIn({ onGoogleSignIn, isLoading, error, onInputChange, onEnterpriseSignIn }) {
  // Add cleanup effect to ensure loading state resets if component unmounts during auth
  useEffect(() => {
    // Create flag to detect if component unmounts during Google sign-in
    let unmounted = false;

    // Cleanup function that runs when component unmounts
    return () => {
      unmounted = true;
      
      // If the component unmounts while Google sign-in is still loading, 
      // DO NOT clear the googleSignInInProgress flag here. 
      // Leaving the flag intact prevents AuthContext from fetching the profile too early.
      if (isLoading) {
        console.log('SocialSignIn unmounted while Google sign-in is in progress – preserving googleSignInInProgress flag');
      }
    };
  }, [isLoading, onGoogleSignIn]);

  // Handler for Google sign-in button click
  const handleGoogleClick = async (e) => {
    e.preventDefault();
    
    // Call the onInputChange function to clear any previous errors
    if (typeof onInputChange === 'function') {
      onInputChange();
    }
    
    // Safety timeout to ensure loading state doesn't get stuck
    const safetyTimeout = setTimeout(() => {
      if (isLoading) {
        console.log('Google sign-in taking too long, resetting loading state');
        // Only clear flag if sign-in did NOT complete
        if (!sessionStorage.getItem('justLoggedIn')) {
          sessionStorage.removeItem('googleSignInInProgress');
        }
        
        // Attempt to reset parent component state through a fake error
        try {
          if (typeof onGoogleSignIn === 'function') {
            onGoogleSignIn().catch(() => {
              // This should be handled by the catch block below
            });
          }
        } catch (error) {
          console.log('Attempted to reset Google sign-in state');
        }
      }
    }, 25000); // 25 seconds safety timeout
    
    try {
      // Add popup close detection
      const handlePopupClose = () => {
        // Check if popup was closed without completing auth
        const popupClosedWithoutAuth = !auth.currentUser || 
          (auth.currentUser && !sessionStorage.getItem('justLoggedIn'));
          
        if (popupClosedWithoutAuth) {
          console.log('Popup may have been closed without completing auth');
          sessionStorage.removeItem('googleSignInInProgress');
        }
      };
      
      // Set a timeout to check if popup was closed without auth completion
      const popupCloseDetector = setTimeout(handlePopupClose, 1000);
      
      // Call the provided onGoogleSignIn function
      await onGoogleSignIn();
      
      // Clear both timeouts
      clearTimeout(popupCloseDetector);
      clearTimeout(safetyTimeout);
    } catch (error) {
      console.error('Error in SocialSignIn Google button click:', error);
      // The error will be handled in the parent component via onGoogleSignIn
      
      // Clear the safety timeout since the error was caught
      clearTimeout(safetyTimeout);
    }
  };

  // Handler for enterprise sign in
  const handleEnterpriseClick = () => {
    if (onInputChange) {
      onInputChange();
    }
    onEnterpriseSignIn();
  };

  return (
    <div className="mx-auto w-[95%] md:w-[85%] max-w-[600px] mt-4">
      {/* Error message removed - it's already shown in the parent SignIn component */}
      
      {/* Divider */}
      <div className="relative mt-4">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
        </div>
        <div className="relative flex justify-center text-sm font-medium leading-6">
          <span className="bg-white dark:bg-gray-900 px-6 text-gray-500 dark:text-gray-400">Or sign in with</span>
        </div>
      </div>

      {/* Google Sign In Button */}
      <div className="mt-4">
        <button
          onClick={handleGoogleClick}
          disabled={isLoading}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-3.5 py-3 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FaGoogle className="h-5 w-5 text-red-500" />
          {isLoading ? 'Signing in...' : 'Sign in with Google'}
        </button>
      </div>

      {/* Enterprise Sign In Button - Right below Google */}
      <div className="mt-3">
        <button
          type="button"
          onClick={handleEnterpriseClick}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-3.5 py-3 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          <Building2 className="h-5 w-5" />
          Sign in with Enterprise Account
        </button>
      </div>

      {/* Don't have an account section */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Don't have an account?
        </p>
        <Link 
          to="/signup" 
          className="mt-2 inline-block font-medium text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 hover:underline"
          onClick={onInputChange} // Clear errors when navigating away
        >
          Sign up for free
        </Link>
      </div>
    </div>
  );
}